import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon, { HelpHint } from '../common/SafeIcon';
import {
  FiSave, FiInfo, FiShield, FiAlertTriangle, FiRefreshCw,
  FiServer, FiGlobe, FiMail
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const emptyForm = {
  id: '',
  name: '',
  clientId: '',
  status: 'active',
  origins: '',
  provider: 'Emailit',
  monthlyLimit: 0,
  fromName: '',
  fromEmail: '',
  replyToEmail: '',
  allowedRecipients: '',
  allowAnyRecipient: false,
  allowDynamicReplyTo: false,
  rateLimitPerMinute: 30,
  notes: ''
};

const AppConfig = () => {
  const [apps, setApps] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assessmentSettings,setAssessmentSettings] = useState({enabled:false,verifyUrl:'https://bpa-verify-otp-service.netlify.app',verifyAppId:'bpas-assessment-hub',notificationEmail:''});
  const [deliverySaving,setDeliverySaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [appsResponse, clientsResponse] = await Promise.all([
        gatewayApi.listApps(),
        gatewayApi.listClients()
      ]);
      const nextApps = appsResponse.data || [];
      setApps(nextApps);
      setClients(clientsResponse.data || []);

      const nextId = selectedAppId && nextApps.some(app => app.id === selectedAppId)
        ? selectedAppId
        : nextApps[0]?.id || '';
      setSelectedAppId(nextId);

      const selected = nextApps.find(app => app.id === nextId);
      if (selected) setForm(toForm(selected));
      else setForm(emptyForm);
    } catch (err) {
      setError(err.message || 'Unable to load app configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const selected = apps.find(app => app.id === selectedAppId);
    if (selected) setForm(toForm(selected));
  }, [selectedAppId, apps]);

  useEffect(()=>{if(!selectedAppId)return;let live=true;gatewayApi.getAssessmentDeliveryConfig(selectedAppId).then(r=>{if(live)setAssessmentSettings({enabled:false,verifyUrl:'https://bpa-verify-otp-service.netlify.app',verifyAppId:'bpas-assessment-hub',notificationEmail:'',...r.data});}).catch(err=>{if(live)setError(err.message||'Unable to read assessment delivery settings.');});return()=>{live=false;};},[selectedAppId]);
  const saveAssessment=async()=>{if(!selectedAppId)return;setDeliverySaving(true);try{await gatewayApi.saveAssessmentDeliveryConfig({appId:selectedAppId,...assessmentSettings});showToast('OTP-verified assessment delivery saved. No Netlify variables were changed.');}catch(err){showToast(err.message||'Unable to save verified assessment delivery.','error');}finally{setDeliverySaving(false);}};
  const clientName = useMemo(
    () => clients.find(client => client.id === form.clientId)?.name || 'Unknown Client',
    [clients, form.clientId]
  );

  const save = async () => {
    if (!form.id) return;
    if (!form.name.trim() || !form.clientId || !form.origins.trim()) {
      showToast('App name, client and allowed origin are required.', 'error');
      return;
    }
    if (!form.allowAnyRecipient && !form.allowedRecipients.trim()) {
      showToast('Configure allowed recipients or enable dynamic recipients.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const response = await gatewayApi.updateApp({
        ...form,
        monthlyLimit: Number(form.monthlyLimit) || 0,
        rateLimitPerMinute: Number(form.rateLimitPerMinute) || 0
      });

      const updated = response.data;
      setApps(prev => prev.map(app => app.id === updated.id ? updated : app));
      setForm(toForm(updated));
      showToast('Application configuration saved to PocketBase.');
    } catch (err) {
      showToast(err.message || 'Unable to save application configuration.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">App Configuration</h2>
          <p className="text-sm text-slate-500">Live origin, recipient and sender policies stored in PocketBase.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none min-w-[220px]"
          >
            {!apps.length && <option value="">No applications</option>}
            {apps.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
          </select>

          <button onClick={load} className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50">
            <SafeIcon icon={FiRefreshCw} />
          </button>

          <button
            onClick={save}
            disabled={!form.id || saving}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertTriangle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4">
        <SafeIcon icon={FiInfo} className="text-blue-500 text-xl shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800 leading-relaxed">
          The application configuration lives in PocketBase. The Emailit API key stays server-side in PocketHost Secrets and is read only by the PocketBase gateway hook; it is never returned to this screen.
        </p>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
          Loading configuration from PocketBase...
        </div>
      ) : !form.id ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
          Create an application first.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card icon={FiGlobe} title="Application & Origin Policy">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Application Name" value={form.name} onChange={value => setForm({ ...form, name: value })} />
                <ReadOnly label="Client" value={clientName} />
                <Select label="Status" value={form.status} onChange={value => setForm({ ...form, status: value })}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="suspended">Suspended</option>
                </Select>
                <Field label="Provider" value={form.provider} onChange={value => setForm({ ...form, provider: value })} />
                <div className="md:col-span-2">
                  <Field
                    label="Allowed Origins"
                    value={form.origins}
                    onChange={value => setForm({ ...form, origins: value })}
                    placeholder="https://example.com, https://www.example.com"
                  />
                </div>
                <Field label="Rate Limit / Minute" type="number" value={form.rateLimitPerMinute} onChange={value => setForm({ ...form, rateLimitPerMinute: value })} />
                <Field label="Monthly App Limit" type="number" value={form.monthlyLimit} onChange={value => setForm({ ...form, monthlyLimit: value })} />
              </div>
            </Card>

            <Card icon={FiShield} title="Recipient & Abuse Controls">
              <Field
                label="Allowed Recipients"
                value={form.allowedRecipients}
                onChange={value => setForm({ ...form, allowedRecipients: value })}
                placeholder="owner@example.com, admin@example.com"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                <Check
                  checked={form.allowAnyRecipient}
                  onChange={checked => setForm({ ...form, allowAnyRecipient: checked })}
                  title="Allow Dynamic Recipients"
                  description="Enable only when the workflow genuinely sends to variable addresses, such as OTP or customer acknowledgements."
                />
                <Check
                  checked={form.allowDynamicReplyTo}
                  onChange={checked => setForm({ ...form, allowDynamicReplyTo: checked })}
                  title="Allow Dynamic Reply-To"
                  description="Useful for contact forms where Reply-To should be the visitor's email address."
                />
              </div>
            </Card>

            <Card icon={FiShield} title="Verified Assessment Delivery">
              <p className="text-xs text-slate-500 mb-4">Configure PDF report delivery for this registered application. BPA Verify checks the email ownership, and the Email Gateway reuses its existing PocketHost provider credentials. No new Netlify environment variables are required.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="BPA Verify URL" value={assessmentSettings.verifyUrl} onChange={verifyUrl=>setAssessmentSettings(v=>({...v,verifyUrl}))}/>
                <Field label="BPA Verify Public App ID" value={assessmentSettings.verifyAppId} onChange={verifyAppId=>setAssessmentSettings(v=>({...v,verifyAppId}))}/>
                <div className="md:col-span-2">
                  <Field label="Internal notification email" type="email" value={assessmentSettings.notificationEmail} onChange={notificationEmail=>setAssessmentSettings(v=>({...v,notificationEmail}))}/>
                </div>
                <div className="md:col-span-2">
                  <Check title="Enable OTP-verified assessment PDF delivery" description="Requires an active app, an active gateway API key, allowed origin and Allow Dynamic Recipients." checked={assessmentSettings.enabled} onChange={enabled=>setAssessmentSettings(v=>({...v,enabled}))}/>
                </div>
              </div>
              <button type="button" disabled={deliverySaving} onClick={saveAssessment} className="mt-4 rounded-xl bg-blue-600 text-white px-5 py-3 text-sm font-semibold disabled:opacity-50">
                {deliverySaving?'Saving delivery settings…':'Save assessment delivery'}
              </button>
            </Card>

            <Card icon={FiMail} title="Sender Identity">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="From Name" value={form.fromName} onChange={value => setForm({ ...form, fromName: value })} />
                <Field label="From Email" type="email" value={form.fromEmail} onChange={value => setForm({ ...form, fromEmail: value })} />
                <Field label="Default Reply-To" type="email" value={form.replyToEmail} onChange={value => setForm({ ...form, replyToEmail: value })} />
              </div>
              <p className="text-[10px] text-slate-400 mt-3">
                Blank sender fields inherit from the client, then from the gateway environment defaults.
              </p>
            </Card>

            <Card icon={FiInfo} title="Internal Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full h-28 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500 resize-none"
                placeholder="Operational notes for this integration..."
              />
            </Card>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-300 flex items-center justify-center">
                <SafeIcon icon={FiServer} />
              </div>
              <h3 className="font-bold mt-4">Server-Side Provider Secret</h3>
              <p className="text-xs text-slate-400 leading-relaxed mt-2">
                Emailit credentials are managed in PocketHost Secrets and are available only to the PocketBase server hook. They are never stored in frontend code or exposed to the browser.
              </p>
              <div className="mt-5 space-y-3">
                <Status label="Email provider secret" value="PocketHost Secret" />
                <Status label="App policy" value="PocketBase" />
                <Status label="API key hashes" value="PocketBase" />
                <Status label="Raw BPA keys" value="Encrypted in Secure Vault (when enabled)" />
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="font-bold text-slate-900">Policy Precedence</h3>
              <ol className="mt-4 space-y-3 text-xs text-slate-600">
                <li><b>1.</b> API-key specific policy</li>
                <li><b>2.</b> Linked application policy</li>
                <li><b>3.</b> Client defaults</li>
                <li><b>4.</b> Gateway environment defaults</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const toForm = (app) => ({
  id: app.id,
  name: app.name || '',
  clientId: app.clientId || '',
  status: app.status || 'active',
  origins: app.origins || '',
  provider: app.provider || 'Emailit',
  monthlyLimit: app.monthlyLimit || 0,
  fromName: app.fromName || '',
  fromEmail: app.fromEmail || '',
  replyToEmail: app.replyToEmail || '',
  allowedRecipients: app.allowedRecipients || '',
  allowAnyRecipient: Boolean(app.allowAnyRecipient),
  allowDynamicReplyTo: Boolean(app.allowDynamicReplyTo),
  rateLimitPerMinute: app.rateLimitPerMinute || 30,
  notes: app.notes || ''
});

const Card = ({ icon, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
      <SafeIcon icon={icon} className="text-slate-400" />
      <h3 className="font-bold text-slate-900">{title}</h3>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500" />
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <div className="mt-2 w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-sm text-slate-600">{value}</div>
  </div>
);

const Select = ({ label, value, onChange, children }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none">
      {children}
    </select>
  </div>
);

const Check = ({ checked, onChange, title, description }) => (
  <div className="flex items-start gap-2"><label className="flex flex-1 gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
    <div>
      <p className="text-xs font-bold text-slate-800">{title}</p>
      <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{description}</p>
    </div>
  </label><HelpHint label={title}/></div>
);

const Status = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-slate-800 pb-2 last:border-0">
    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
    <span className="text-[10px] font-bold text-emerald-300">{value}</span>
  </div>
);

export default AppConfig;